import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import ServicesUserInfo from '../services-user-info/services-user-info.component';
import ServicesGuestInfo from '../services-guest-info/services-guest-info.component';
import Button from '../../../common/button/button.component';
import {
  ServicesMenuBody,
  ServicesMenuContainer,
  ServicesMenuHeader,
  Services,
  Section,
  SectionTitle,
  ServicesSubItems,
  ServicesQRIconLabel,
  DownloadQrContainer,
} from './services-menu.styles';
import { useStore } from '../../../stores/store';
import { useStartParcelCreation } from '../../../hooks/use-start-parcel-creation.hook';
import { AppRoute } from '../../../constants/route';
import { BlurredMenuOverlay } from '../../../common/blurred-background/blurred-background.styles';
import { generateMyRoute } from '../../../utils/uri/uri.utils';
import { useIsMobile } from '../../../hooks/use-is-mobile.hook';
import {
  fullMenuItems,
  ServiceId,
  ServicesSubMenuItemId,
} from '../services-menu-item/config';
import ServicesMenuItem from '../services-menu-item/services-menu-item.component';
import SwitchToOldPortalButton from '../services-menu-item/switch-to-old-portal-button.component';
import { useModal } from '../../../common/modal';
import { Modals } from '../../../constants/modals';
import { isFeatureRequired, isMeest } from '../../../utils/client/client.utils';
import { client } from '../../../config';
import Separator from '../../../common/separator/separator.component';
import HeaderContacts from '../../header/header-contacts/header-contacts.component';
import QrCode from '../../../common/qr-code/qr-code.component';
import { testIds } from '../../../../test-ids';
import CrossSaleMenuSection from '../cross-sale-menu-section/cross-sale-menu-section.component';
import { ReactComponent as WarningIcon } from '../../../assets/warning-tip.svg';

const ServicesMenu = () => {
  const {
    userStore: { user, logout, loginGup },
    navStore: {
      selectedService,
      isLeftSidebarOpened,
      toggleLeftSidebar,
      setSelectedService,
      setSelectedMenuSections,
    },
    parcelCreationStore: { isParcelCreationInProgress },
    commonStore: { isGuestParcelCreation, analyticsManager },
    feedbackStore: { setSendFrom, isFeedbackInProgress },
    marketingStore: { countriesWithoutOldPortal, appDownloadLink },
    localizationsStore: { selectedCountry },
  } = useStore();
  const location = useLocation();
  const handleStartParcelCreation = useStartParcelCreation();
  const isMobile = useIsMobile();
  const { pathname } = location;
  const { openModal, closeModal } = useModal();

  const { t } = useTranslation();

  const servicesMenuHeaderRef = useRef<HTMLDivElement | null>(null);

  const auth = useAuth();

  useEffect(
    () => () => {
      setSelectedService(undefined);
      setSelectedMenuSections([]);
    },
    // eslint-disable-next-line
    []
  );

  useEffect(() => {
    fullMenuItems.forEach(({ id, items }) => {
      const route = items?.find(({ id: innerId }) =>
        pathname.includes(innerId)
      );
      if (route) {
        setSelectedService(`${id}:${route.id}`);
      }
      if (items?.length === 0 && pathname.includes(id)) {
        setSelectedService(id);
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isUserExist = user !== null;

  const isMeestClient = isMeest(client);

  const isParcelCreate = location.pathname.includes(AppRoute.PARCEL_CREATE);

  const isShowOldPortal =
    selectedCountry?.country_code &&
    !countriesWithoutOldPortal.includes(selectedCountry?.country_code);

  const omitDownloadApp = isFeatureRequired('hideMobilAppInfo');

  const handleSelectMenuItem = (
    serviceId: ServiceId,
    subItemId: ServicesSubMenuItemId
  ) => {
    if (subItemId === 'leavefeedback') {
      setSendFrom('from_settings');
    }
    setSelectedService(serviceId);
    isLeftSidebarOpened && toggleLeftSidebar();
  };

  const openmodalQR = () => {
    openModal({
      id: Modals.QR_ICON,
      name: Modals.QR_ICON,
      props: {
        onClose: () => {
          closeModal(Modals.QR_ICON);
        },
        qrValue: appDownloadLink,
      },
    });
  };

  return (
    <ServicesMenuContainer
      data-testid={isMobile ? testIds.header.userMenu : null}>
      <ServicesMenuHeader ref={servicesMenuHeaderRef}>
        {!isGuestParcelCreation && (
          <>
            <ServicesUserInfo />
            {!isParcelCreate && (
              <Button
                disabled={!isUserExist}
                testId={testIds.sidebarMenu.newParcel}
                onClick={() => {
                  analyticsManager.track('start_new_delivery');
                  handleStartParcelCreation();
                }}
                icon={{ glyph: 'plus' }}>
                {t('new_parcel')}
              </Button>
            )}
          </>
        )}
        {isGuestParcelCreation && (
          <>
            <ServicesGuestInfo />
            <Button
              onClick={() => {
                loginGup({
                  auth,
                  redirectUri: location.pathname,
                  isGuestFlow: true,
                });
              }}
              appearance='secondary'>
              {t('sign_up')}
            </Button>
          </>
        )}
      </ServicesMenuHeader>
      <ServicesMenuBody>
        <Services>
          {fullMenuItems.map((item) => (
            <Section key={item.id}>
              <SectionTitle>{t(item.title)}</SectionTitle>
              <ServicesSubItems>
                {item.items?.map((subItem) => {
                  const serviceId: ServiceId = `${item.id}:${subItem.id}`;
                  return (
                    <ServicesMenuItem
                      to={subItem.route ? generateMyRoute(subItem.route) : ''}
                      isDisabled={!user || !!isGuestParcelCreation}
                      isSelected={serviceId === selectedService}
                      onClick={() =>
                        handleSelectMenuItem(serviceId, subItem.id)
                      }
                      icon={subItem.icon}
                      endAdornment={
                        subItem.id === 'personaldata' && user && !user.phone ? (
                          <WarningIcon aria-hidden />
                        ) : undefined
                      }
                      key={subItem.id}>
                      {t(subItem.title)}
                    </ServicesMenuItem>
                  );
                })}
              </ServicesSubItems>
            </Section>
          ))}
          {isFeatureRequired('otherProductsRequired') && (
            <CrossSaleMenuSection />
          )}
          {isMobile && (
            <ServicesMenuItem
              appearance='logout'
              icon='logout'
              onClick={async () => {
                await logout({
                  omitApiLogout: false,
                  interruptLogoutAfterRedirect:
                    isParcelCreationInProgress || isFeedbackInProgress,
                });
                toggleLeftSidebar();
              }}>
              {t('logout')}
            </ServicesMenuItem>
          )}
          {isMobile && (
            <>
              <Separator
                mt='0'
                ml='10px'
                mr='10px'
              />
              <HeaderContacts />
            </>
          )}
          <DownloadQrContainer $hidden={omitDownloadApp}>
            <ServicesQRIconLabel>{t('meest_in_phone')}</ServicesQRIconLabel>
            <QrCode
              value={appDownloadLink}
              useBrandColor
              size={40}
              onClick={() => openmodalQR()}
            />
          </DownloadQrContainer>

          {isMeestClient && isShowOldPortal && (
            <SwitchToOldPortalButton href='https://www.mymeest.ca/MeestPortal/login'>
              {t('switch_to_old_portal')}
            </SwitchToOldPortalButton>
          )}
        </Services>
        {isGuestParcelCreation && <BlurredMenuOverlay />}
      </ServicesMenuBody>
    </ServicesMenuContainer>
  );
};

export default observer(ServicesMenu);
